import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getRecruits, type Recruit, hoursSince } from '../api/recruits';

const STAGES = [
  'application',
  'screening',
  'interview',
  'offer',
  'contract',
  'onboarding',
  'training',
  'licensing',
  'live',
];

type SortKey = 'name' | 'stage' | 'days' | 'comp' | 'created';

export default function RecruitingRecruits() {
  const [recruits, setRecruits] = useState<Recruit[]>([]);
  const [stage, setStage] = useState<string>('');
  const [q, setQ] = useState<string>('');
  const [sort, setSort] = useState<SortKey>('created');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {};
      if (stage) params.stage = stage;
      if (q) params.q = q;
      const { recruits } = await getRecruits(params);
      setRecruits(recruits);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  const sorted = useMemo(() => {
    const copy = [...recruits];
    copy.sort((a, b) => {
      switch (sort) {
        case 'name':
          return `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`);
        case 'stage':
          return STAGES.indexOf(a.stage) - STAGES.indexOf(b.stage);
        case 'days':
          return hoursSince(b.stageEnteredAt) - hoursSince(a.stageEnteredAt);
        case 'comp':
          return b.compPercent - a.compPercent;
        case 'created':
        default:
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
    });
    return copy;
  }, [recruits, sort]);

  return (
    <div className="px-6 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="h-display text-3xl">Recruits</h1>
            <p className="text-dg-mist text-sm mt-1">All recruits across all stages.</p>
          </div>
          <Link
            to="/recruiting/applications/new"
            className="bg-dg-red hover:bg-dg-red-hover text-white text-sm font-medium px-4 py-2 rounded-md transition"
          >
            + New Application
          </Link>
        </div>

        <div className="flex items-center gap-3 mb-4">
          <input
            placeholder="Search by name, email, phone…"
            className="bg-dg-panel border border-dg-border rounded-md px-3 py-2 text-sm w-72 focus:outline-none focus:border-dg-red/60"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
          />
          <select
            value={stage}
            onChange={(e) => setStage(e.target.value)}
            className="bg-dg-panel border border-dg-border rounded-md px-3 py-2 text-sm"
          >
            <option value="">All stages</option>
            {STAGES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortKey)}
            className="bg-dg-panel border border-dg-border rounded-md px-3 py-2 text-sm"
          >
            <option value="created">Sort: Newest</option>
            <option value="name">Sort: Name</option>
            <option value="stage">Sort: Stage</option>
            <option value="days">Sort: Days in stage</option>
            <option value="comp">Sort: Comp %</option>
          </select>
        </div>

        {error && <div className="text-red-400 mb-3 text-sm">{error}</div>}
        {loading ? (
          <div className="text-dg-mist">Loading…</div>
        ) : sorted.length === 0 ? (
          <div className="text-dg-mist text-sm py-12 text-center bg-dg-panel/40 rounded-lg border border-dg-border">
            No recruits yet.
          </div>
        ) : (
          <div className="bg-dg-panel/60 border border-dg-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-dg-mist border-b border-dg-border">
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2">Stage</th>
                  <th className="px-4 py-2">State</th>
                  <th className="px-4 py-2">Comp</th>
                  <th className="px-4 py-2">Days in stage</th>
                  <th className="px-4 py-2">Email</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => (
                  <tr
                    key={r._id}
                    onClick={() => navigate(`/recruiting/recruits/${r._id}`)}
                    className="border-b border-dg-border last:border-0 hover:bg-white/5 cursor-pointer"
                  >
                    <td className="px-4 py-2 font-medium">{r.firstName} {r.lastName}</td>
                    <td className="px-4 py-2 capitalize">{r.stage}</td>
                    <td className="px-4 py-2 uppercase">{r.state}</td>
                    <td className="px-4 py-2">{r.compPercent}%</td>
                    <td className="px-4 py-2">{Math.round(hoursSince(r.stageEnteredAt) / 24)}d</td>
                    <td className="px-4 py-2 text-dg-mist">{r.email}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}
